/**
 * ConsentPopover - поповер согласия с условиями использования
 *
 * Появляется над виджетом при первом нажатии на voice/chat.
 * После подтверждения запускает соответствующее действие.
 */

import { memo, useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import { WidgetLink } from './WidgetLink';
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from './legalDefaults';
import './ConsentPopover.css';

export const ConsentPopover = memo(({
  onConfirm,
  onClose,
  termsUrl = DEFAULT_TERMS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL
}) => {
  const popoverRef = useRef(null);

  // Flip вверх→вниз если нет места сверху, затем горизонтальный сдвиг если выходит за край
  useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const MARGIN = 8;

    // 1. Вертикальный flip: если поповер обрезается сверху — открываем под виджетом
    const rect = el.getBoundingClientRect();
    if (rect.top < MARGIN) {
      el.classList.add('consent-popover--flipped');
    }

    // 2. Горизонтальный сдвиг: перемещаем внутрь вьюпорта, стрелка компенсирует
    const finalRect = el.getBoundingClientRect();
    let shift = 0;
    if (finalRect.left < MARGIN) {
      shift = MARGIN - finalRect.left;
    } else if (finalRect.right > window.innerWidth - MARGIN) {
      shift = (window.innerWidth - MARGIN) - finalRect.right;
    }
    if (shift !== 0) {
      el.style.setProperty('--popover-shift', `${shift}px`);
    }
  }, []);

  // Закрытие при клике вне поповера
  // e.composedPath() используется вместо e.target для корректной работы в Shadow DOM:
  // в Shadow DOM e.target ретаргетится на shadow host, поэтому contains(e.target) всегда false
  useEffect(() => {
    const handleClickOutside = (e) => {
      const path = e.composedPath ? e.composedPath() : [e.target];
      if (popoverRef.current && !path.includes(popoverRef.current) && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    // setTimeout чтобы не ловить тот же клик который открыл поповер
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  // Закрытие по Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="consent-popover" ref={popoverRef}>
      <p className="consent-popover__text">
        Please confirm your consent to audio recording, as well as our{' '}
        <WidgetLink href={termsUrl}>Terms of Service</WidgetLink>
        {' '}and{' '}
        <WidgetLink href={privacyUrl}>Privacy Policy</WidgetLink>
      </p>
      <div className="consent-popover__actions">
        <Button
          className="consent-popover__confirm"
          size="sm"
          onClick={onConfirm}
        >
          Consent
        </Button>
      </div>
    </div>
  );
});

ConsentPopover.displayName = 'ConsentPopover';

ConsentPopover.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  termsUrl: PropTypes.string,
  privacyUrl: PropTypes.string,
};
